import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import Icon from 'flow/components/ui-components/Icon';
import Button from 'flow/components/ui-components/Button';
import { Tooltip } from 'flow/components/ui-components/Tooltip';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    markCallRecordHandled,
    unmarkCallRecordHandled,
} from 'flow/actions/call-record-actions';
import { segmentTrack } from 'flow/actions/segment-actions';
import { CALL_HISTORY_STRINGS } from 'flow/utils/segment-utils';
import {
    getCallRecordActionMarkedAsHandled,
    callRecordIsMissed,
} from 'flow/utils/call-records-utils';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import { getContactName } from 'flow/utils/contact-utils';
import { getDateDescription } from 'flow/utils';
import { CallRecord, CallRecordAction } from 'flow/types/CallRecord';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { SegmentContextType } from 'flow/components/call-history-v2/types';

interface Props {
    callRecordKey: CallRecordKey;
    segmentContext: SegmentContextType;
}

const MarkAsHandledButtonV2 = ({ callRecordKey, segmentContext }: Props) => {
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );

    const callAction = useMemo(
        () =>
            callRecord
                ? getCallRecordActionMarkedAsHandled(callRecord)
                : undefined,
        [callRecord],
    );

    if (!callRecord) return null;

    const queueKey = callRecord.getIn(['queue', 'key']);
    const callRecordMissed = callRecordIsMissed(callRecord);

    if (!callRecordMissed) {
        return null;
    }

    const button = callAction ? (
        <UnmarkButton
            callRecord={callRecord}
            callAction={callAction}
            queueKey={queueKey}
            segmentContext={segmentContext}
        />
    ) : (
        <MarkButton
            callRecord={callRecord}
            queueKey={queueKey}
            segmentContext={segmentContext}
        />
    );

    return <div className="@container">{button}</div>;
};

export default MarkAsHandledButtonV2;

interface MarkButtonProps {
    callRecord: TypedMap<CallRecord>;
    queueKey?: QueueKey;
    segmentContext: SegmentContextType;
}

const MarkButton = ({
    callRecord,
    queueKey,
    segmentContext,
}: MarkButtonProps) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const dispatch = useAppDispatch();

    const onClick = () => {
        dispatch(
            segmentTrack(CALL_HISTORY_STRINGS.CLICK_MARK_AS_HANDLED, {
                component: segmentContext,
            }),
        );
        openModal('confirmationDialog', {
            title: i18next.t('Queue.History.MarkAsHandled'),
            description: (
                <div className="min-h-16">
                    {i18next.t('Queue.History.Mark.Message')}
                </div>
            ),
            confirmButtonText: i18next.t('Queue.History.Mark'),
            handleAccept: () => {
                dispatch(
                    segmentTrack(
                        CALL_HISTORY_STRINGS.CLICK_CONFIRM_MARK_AS_HANDLED,
                        { component: segmentContext },
                    ),
                );
                dispatch(markCallRecordHandled(callRecord, queueKey));
            },
        });
    };

    return (
        <Button
            className="h-9 p-2 text-sm font-normal text-gray-800"
            onClick={onClick}
            color="gray"
        >
            {i18next.t('Queue.History.MarkAsHandled')}
        </Button>
    );
};

interface UnmarkButtonProps {
    callRecord: TypedMap<CallRecord>;
    callAction: TypedMap<CallRecordAction>;
    queueKey?: QueueKey;
    segmentContext: SegmentContextType;
}

const UnmarkButton = ({
    callRecord,
    callAction,
    queueKey,
    segmentContext,
}: UnmarkButtonProps) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const dispatch = useAppDispatch();

    const agentName = getContactName(callAction.getIn(['agent', 'contact']));
    const actionTime = getDateDescription(
        callAction.get('timestamp'),
        i18next,
        true,
    );

    const label = agentName
        ? `${agentName} ${actionTime}`
        : `${i18next.t('call.handled')} ${actionTime}`;

    const tooltipLabel = agentName ? (
        <Trans
            i18n={i18next}
            i18nKey="Queue.History.HandledByAt"
            values={{ agent: agentName, actionTime }}
            components={[<strong key="agent" />]}
        />
    ) : undefined;

    const onClick = () => {
        dispatch(
            segmentTrack(CALL_HISTORY_STRINGS.CLICK_UNMARK_AS_HANDLED, {
                component: segmentContext,
            }),
        );
        openModal('confirmationDialog', {
            title: i18next.t('Queue.History.UnmarkAsHandled'),
            description: (
                <div className="min-h-16">
                    {i18next.t('Queue.History.Unmark.Message')}
                </div>
            ),
            confirmButtonText: i18next.t('Queue.History.Unmark'),
            handleAccept: () => {
                dispatch(
                    segmentTrack(
                        CALL_HISTORY_STRINGS.CLICK_CONFIRM_UNMARK_AS_HANDLED,
                        { component: segmentContext },
                    ),
                );
                dispatch(unmarkCallRecordHandled(callRecord, queueKey));
            },
        });
    };

    const button = (
        <Button
            className="flex min-h-9 items-center gap-2 p-2 text-sm/5 font-normal text-gray-800"
            onClick={onClick}
            color="gray"
        >
            <Icon
                iconKey="success"
                size="1rem"
                className="text-green shrink-0"
            />
            <span className="hidden @sm:inline">{label}</span>
            {agentName && <span className="@sm:hidden">{agentName}</span>}
        </Button>
    );

    if (!tooltipLabel) return button;

    return (
        <Tooltip label={tooltipLabel} tooltipClassName="w-fit">
            {button}
        </Tooltip>
    );
};
